import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Alert, Spinner, cx } from "@/components/ui";
import {
  REGIONS,
  type BreakdownDimension,
  type BreakdownRow,
  type BreakdownWindow,
  type ProgressWindow,
  type Region,
} from "@/api/stats-types";
import { getGlobalStats, getStatsTree } from "@/api/stats";
import {
  getActivity,
  getLearnerStats,
  getLearnerTree,
  getLearnerTrend,
  getMistakes,
  getProgression,
  getStatsBreakdown,
} from "@/api/learner";
import { TreeView } from "./tree-view";
import { WorldMap } from "./world-map";
import { TrendChart } from "./trend-chart";
import { flagClasses, num, pct, pct100, prettySlug, tierLabel } from "./format";

type Tab = "you" | "global";

export function StatsPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("tab") === "global" ? "global" : "you");
  return (
    <div className="animate-fade-up">
      <h1 className="text-3xl font-bold tracking-tight">{t("Statistics")}</h1>
      <p className="mt-1 text-ink-faint">
        {tab === "you"
          ? t("Your progress on EduPause.")
          : t("How the world learns on EduPause.")}
      </p>

      <div className="mt-5 inline-flex rounded-xl bg-black/5 p-1">
        {(
          [
            ["you", t("You")],
            ["global", t("World")],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cx(
              "rounded-lg px-4 py-1.5 text-sm font-semibold transition",
              tab === key ? "bg-white text-ink shadow-soft" : "text-ink-soft hover:text-ink",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">{tab === "you" ? <YourStats /> : <GlobalStats />}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function StatCard({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-2xl font-bold tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-sm text-ink-faint">{label}</div>
      {sub != null && <div className="mt-1 text-xs text-ink-faint/80">{sub}</div>}
    </div>
  );
}

function ActivityCard({ title, w, loading }: { title: string; w?: ProgressWindow; loading: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="card p-4">
      <div className="text-sm font-semibold text-ink">{title}</div>
      {loading || !w ? (
        <div className="mt-2 text-sm text-ink-faint">—</div>
      ) : (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <span className="text-2xl font-bold text-ink">{num(w.answered)}</span>
          <span className="text-sm text-ink-faint">{t("answered")}</span>
          <span className="text-sm text-ink-faint">{t("{{pct}} correct", { pct: pct100(w.accuracy_percent) })}</span>
          <span className="text-sm text-ink-faint">+{num(w.xp_earned)} XP</span>
          <span className="text-sm text-ink-faint">{t("+{{mins}} min", { mins: num(w.minutes_earned) })}</span>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-16 text-brand-600">
      <Spinner className="h-7 w-7" />
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly (readonly [T, string])[];
}) {
  return (
    <div className="inline-flex rounded-xl bg-black/5 p-1">
      {options.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cx(
            "rounded-lg px-3 py-1.5 text-sm font-semibold transition",
            value === key ? "bg-white text-ink shadow-soft" : "text-ink-soft hover:text-ink",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Strengths & gaps — adjustable, windowed accuracy ranked three ways.
// Replaces the old static "Topics — weakest first" card. Archived /
// never-practiced sets are excluded server-side (toggle to include archived).
// ---------------------------------------------------------------------------

const WEAK_ACC = 0.5;
const STRONG_ACC = 0.8;

function accFlag(a: number): "weak" | "strong" | "neutral" {
  if (a < WEAK_ACC) return "weak";
  if (a > STRONG_ACC) return "strong";
  return "neutral";
}

function StrengthsGaps() {
  const { t } = useTranslation();
  const dimensions = [
    ["by_topic", t("Topics")],
    ["by_set", t("Sets")],
    ["by_framework", t("Frameworks")],
  ] as const satisfies readonly (readonly [BreakdownDimension, string])[];
  const windows = [
    ["7", t("7 days")],
    ["30", t("30 days")],
    ["all", t("All time")],
  ] as const satisfies readonly (readonly [BreakdownWindow, string])[];
  const [dim, setDim] = useState<BreakdownDimension>("by_topic");
  const [win, setWin] = useState<BreakdownWindow>("30");
  const [includeArchived, setIncludeArchived] = useState(false);
  // "Pick what counts": keys the user has unchecked out of the ranking.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["stats-breakdown", win, includeArchived],
    queryFn: () => getStatsBreakdown(win, includeArchived),
  });

  // A fresh dimension/window/archived view starts with everything included.
  useEffect(() => {
    setExcluded(new Set());
  }, [dim, win, includeArchived]);

  const rows = q.data?.[dim] ?? [];
  const labelFor = (r: BreakdownRow) => (dim === "by_set" ? r.label : prettySlug(r.label));
  const visible = rows
    .filter((r) => !excluded.has(r.key))
    .slice()
    .sort((a, b) => a.accuracy - b.accuracy);

  const toggle = (key: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Section
      title={t("Strengths & gaps")}
      aside={<Segmented value={win} onChange={setWin} options={windows} />}
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Segmented value={dim} onChange={setDim} options={dimensions} />
        {dim !== "by_topic" && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="h-4 w-4 rounded border-black/20 text-brand-600 focus:ring-brand-400"
            />
            {t("Include archived")}
          </label>
        )}
      </div>

      {/* Pick what counts */}
      {rows.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {rows.map((r) => {
            const on = !excluded.has(r.key);
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => toggle(r.key)}
                className={cx(
                  "rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition",
                  on
                    ? "bg-brand-50 text-brand-700 ring-brand-200"
                    : "bg-black/[0.03] text-ink-faint line-through ring-black/10",
                )}
              >
                {labelFor(r)}
              </button>
            );
          })}
        </div>
      )}

      {q.isLoading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <p className="rounded-xl bg-black/[0.02] px-4 py-6 text-center text-sm text-ink-faint">
          {rows.length === 0
            ? t("Practice a few sets to see your strengths and gaps.")
            : t("Nothing selected — re-enable a chip above.")}
        </p>
      ) : (
        <div className="card divide-y divide-black/5">
          {visible.map((r) => {
            const flag = accFlag(r.accuracy);
            return (
              <div key={r.key} className="flex items-center gap-3 px-4 py-2.5">
                <span
                  className={cx(
                    "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1",
                    flagClasses(flag),
                  )}
                >
                  {t(flag)}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium text-ink">
                  {labelFor(r)}
                  {r.archived && <span className="ml-2 text-xs text-ink-faint">{t("archived")}</span>}
                  {dim === "by_set" && r.framework_slug && (
                    <span className="ml-2 text-xs text-ink-faint">
                      {prettySlug(r.framework_slug)}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-sm text-ink-faint">
                  {t("{{pct}} · {{answered}} answered", { pct: pct(r.accuracy), answered: num(r.attempts) })}
                  {dim === "by_framework" && r.set_count != null && (
                    <> · {t("{{sets}} sets", { sets: num(r.set_count) })}</>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Your stats
// ---------------------------------------------------------------------------

function YourStats() {
  const { t } = useTranslation();
  const progression = useQuery({ queryKey: ["progression"], queryFn: getProgression });
  const stats = useQuery({ queryKey: ["learner-stats"], queryFn: getLearnerStats });
  const tree = useQuery({ queryKey: ["learner-tree", null], queryFn: () => getLearnerTree() });
  const trend = useQuery({ queryKey: ["learner-trend"], queryFn: getLearnerTrend });
  const weekly = useQuery({ queryKey: ["activity", "weekly"], queryFn: () => getActivity("weekly") });
  const monthly = useQuery({ queryKey: ["activity", "monthly"], queryFn: () => getActivity("monthly") });
  const total = useQuery({ queryKey: ["activity", "total"], queryFn: () => getActivity("total") });
  const mistakes = useQuery({ queryKey: ["mistakes"], queryFn: getMistakes });

  if (progression.isLoading) return <Loading />;
  if (progression.isError) {
    return <Alert>{(progression.error as Error)?.message || t("Couldn't load your stats.")}</Alert>;
  }

  const p = progression.data!;
  const weak = new Set(stats.data?.weak_topics ?? []);
  const topics = [...(stats.data?.topics ?? [])].sort(
    (a, b) => (a.percentile ?? 999) - (b.percentile ?? 999),
  );
  // All-time answered: source from the activity counter (raw question_attempts)
  // so it agrees with Recent activity, not the topic-scoped /learner/stats sum.
  const totalAnswered = total.data?.answered ?? topics.reduce((a, t) => a + t.n_attempts, 0);

  return (
    <div>
      {/* Rank header */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("Rank")} value={tierLabel(p.tier)} sub={`${num(p.total_xp)} XP`} />
        <StatCard
          label={t("Current streak")}
          value={`${p.current_streak}🔥`}
          sub={t("Best {{count}}", { count: p.longest_streak })}
        />
        <StatCard
          label={t("Topics tracked")}
          value={num(stats.data?.topics.length ?? 0)}
          sub={t("{{count}} need work", { count: weak.size })}
        />
        <StatCard
          label={t("Answered (all-time)")}
          value={num(totalAnswered)}
          sub={t("across {{count}} topics", { count: topics.length })}
        />
      </div>

      {/* Recent activity */}
      <Section title={t("Recent activity")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <ActivityCard title={t("This week")} w={weekly.data} loading={weekly.isLoading} />
          <ActivityCard title={t("This month")} w={monthly.data} loading={monthly.isLoading} />
        </div>
      </Section>

      {/* Adjustable strengths & gaps (replaces the old static topic list) */}
      <StrengthsGaps />

      {/* Progress over time */}
      <Section title={t("Progress over time")}>
        {trend.isLoading ? <Loading /> : <TrendChart topics={trend.data?.topics ?? []} />}
      </Section>

      {/* Your knowledge tree — your own topics, independent of the global floor */}
      <Section title={t("Your knowledge tree")}>
        {tree.isLoading ? (
          <Loading />
        ) : (
          <TreeView
            nodes={tree.data?.nodes ?? []}
            showUserLevel
            emptyMessage={t("Practice a set to start growing your knowledge tree.")}
          />
        )}
      </Section>

      {/* Mistakes */}
      <Section title={t("Most-missed topics")}>
        {mistakes.isLoading ? (
          <Loading />
        ) : (mistakes.data?.topics?.length ?? 0) === 0 ? (
          <p className="rounded-xl bg-black/[0.02] px-4 py-6 text-center text-sm text-ink-faint">
            {t("No recurring mistakes yet — nice.")}
          </p>
        ) : (
          <div className="card divide-y divide-black/5">
            {mistakes.data!.topics.map((m) => (
              <div key={m.topic_slug} className="flex items-center gap-3 px-4 py-2.5">
                <span className="min-w-0 flex-1 truncate font-medium text-ink">
                  {prettySlug(m.topic_slug)}
                </span>
                <span className="shrink-0 text-sm text-ink-faint">
                  {t("missed {{miss}}/{{total}}", { miss: m.miss_count, total: m.attempt_count })}
                  {m.miss_rate != null && ` · ${pct(m.miss_rate)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Global stats
// ---------------------------------------------------------------------------

function GlobalStats() {
  const { t } = useTranslation();
  const [region, setRegion] = useState<Region | null>(null);

  const global = useQuery({
    queryKey: ["global-stats", region],
    queryFn: () => getGlobalStats(region ?? undefined),
  });
  const tree = useQuery({
    queryKey: ["stats-tree", region],
    queryFn: () => getStatsTree(region ?? undefined),
  });

  // Per-region activity for the map bubbles (root learner counts).
  const regionTrees = useQueries({
    queries: REGIONS.map((r) => ({
      queryKey: ["stats-tree", r],
      queryFn: () => getStatsTree(r),
    })),
  });
  const values: Partial<Record<Region, number>> = {};
  REGIONS.forEach((r, i) => {
    const data = regionTrees[i]?.data;
    if (data) values[r] = data.nodes.reduce((a, n) => a + n.user_count, 0);
  });

  if (global.isLoading) return <Loading />;
  if (global.isError) {
    return <Alert>{(global.error as Error)?.message || t("Couldn't load global stats.")}</Alert>;
  }
  const g = global.data!;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("Active learners")} value={num(g.total_active_users)} />
        <StatCard label={t("Questions answered")} value={num(g.total_questions_answered)} />
        <StatCard label={t("Overall accuracy")} value={pct100(g.overall_accuracy_percent)} />
        <StatCard label={t("Sets generated")} value={num(g.total_sets_generated)} />
      </div>

      <Section title={t("How the world learns")}>
        <WorldMap values={values} selected={region} onSelect={setRegion} />
      </Section>

      <Section
        title={t("Knowledge tree")}
        aside={
          <span className="text-sm text-ink-faint">{region ? region : t("Worldwide")}</span>
        }
      >
        {tree.isLoading ? <Loading /> : <TreeView nodes={tree.data?.nodes ?? []} />}
      </Section>

      <Section title={t("Most-studied topics")}>
        {g.by_topic.length === 0 ? (
          <p className="rounded-xl bg-black/[0.02] px-4 py-6 text-center text-sm text-ink-faint">
            {t("No topic data yet.")}
          </p>
        ) : (
          <div className="card divide-y divide-black/5">
            {g.by_topic.slice(0, 12).map((tp) => (
              <div key={tp.topic_slug} className="flex items-center gap-3 px-4 py-2.5">
                <span className="min-w-0 flex-1 truncate font-medium text-ink">
                  {prettySlug(tp.topic_slug)}
                </span>
                <span className="shrink-0 text-sm text-ink-faint">
                  {t("{{answered}} answered", { answered: num(tp.questions_answered) })} · {t("{{pct}} correct", { pct: pct100(tp.accuracy_percent) })}
                  {tp.unique_users != null && ` · ${t("{{learners}} learners", { learners: num(tp.unique_users) })}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
